import { useEffect, useState } from 'react';
import {
  configApiRef,
  discoveryApiRef,
  fetchApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import CircularProgress from '@material-ui/core/CircularProgress';
import InputAdornment from '@material-ui/core/InputAdornment';
import Typography from '@material-ui/core/Typography';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Button from '@material-ui/core/Button';

export interface UserGroupOption {
  id: string;
  displayName: string;
  spokeName: string;
}

interface UserGroupMember {
  id: string;
  displayName: string;
  email: string;
}

/**
 * Dropdown of the persona groups administered by the signed-in user.
 *
 * The user is identified automatically from their Backstage identity (no
 * manual email entry); the backend `azure` plugin resolves the group
 * admin membership and discovers same-spoke persona groups via Microsoft Graph
 * using its managed identity. The user need not belong to those persona groups.
 */
export const UserGroupsField = ({
  onChange,
  rawErrors,
  required,
  formData,
  schema,
  uiSchema,
}: FieldExtensionComponentProps<string>) => {
  const identityApi = useApi(identityApiRef);
  const configApi = useApi(configApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const [options, setOptions] = useState<UserGroupOption[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<string>();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | undefined>();
  const [userEmail, setUserEmail] = useState<string>();
  const [members, setMembers] = useState<UserGroupMember[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersError, setMembersError] = useState<string>();
  const [memberEmail, setMemberEmail] = useState('');
  const [addMemberLoading, setAddMemberLoading] = useState(false);
  const [addMemberError, setAddMemberError] = useState<string>();
  const showMemberManagement =
    (uiSchema?.['ui:options'] as { manageUsers?: boolean } | undefined)
      ?.manageUsers !== false;

  useEffect(() => {
    let cancelled = false;

    const loadGroups = async () => {
      try {
        const profile = await identityApi.getProfileInfo();
        let email = profile.email?.trim();

        // The guest sign-in identity used for Playwright E2E runs has no
        // catalog-backed profile, so it never carries an email (mirrors
        // RequestedByField's fallback for the same reason).
        if (!email && configApi.getOptionalBoolean('scaffolder.e2eTestMode')) {
          email = 'e2e-test-user@howdengroup.com';
        }

        if (!email) {
          throw new Error('Signed-in user has no email address');
        }
        if (!cancelled) {
          setUserEmail(email);
        }

        const azureBase = await discoveryApi.getBaseUrl('azure');
        const response = await fetchApi.fetch(
          `${azureBase}/users/groups?email=${encodeURIComponent(email)}`,
        );
        if (!response.ok) {
          throw new Error(`${response.status}`);
        }
        const groups: UserGroupOption[] = await response.json();
        if (!cancelled) {
          setOptions(groups);
        }
      } catch {
        if (!cancelled) {
          setLoadError('Failed to load your user groups');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadGroups();
    return () => {
      cancelled = true;
    };
  }, [identityApi, configApi, discoveryApi, fetchApi]);

  const hasError = Boolean(rawErrors?.length) || Boolean(loadError);
  const noGroups = !loading && !loadError && options.length === 0;
  const selectedGroup =
    options.find(group => group.id === selectedGroupId) ??
    options.find(group => group.displayName === formData);

  const selectGroup = async (groupId: string, groupName: string) => {
    setSelectedGroupId(groupId || undefined);
    onChange(groupName || undefined);
    setMembers([]);
    setMembersError(undefined);
    setAddMemberError(undefined);
    setMemberEmail('');
    if (!showMemberManagement || !groupId || !userEmail) {
      return;
    }

    setMembersLoading(true);
    try {
      const azureBase = await discoveryApi.getBaseUrl('azure');
      const response = await fetchApi.fetch(
        `${azureBase}/users/groups/${encodeURIComponent(
          groupId,
        )}/members?email=${encodeURIComponent(userEmail)}`,
      );
      if (!response.ok) {
        throw new Error(`${response.status}`);
      }
      setMembers(await response.json());
    } catch {
      setMembersError('Failed to load users in this group');
    } finally {
      setMembersLoading(false);
    }
  };

  const addMember = async () => {
    if (!selectedGroup || !userEmail || !memberEmail.trim()) {
      return;
    }

    setAddMemberLoading(true);
    setAddMemberError(undefined);
    try {
      const azureBase = await discoveryApi.getBaseUrl('azure');
      const response = await fetchApi.fetch(
        `${azureBase}/users/groups/${encodeURIComponent(
          selectedGroup.id,
        )}/members?email=${encodeURIComponent(userEmail)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: memberEmail.trim() }),
        },
      );
      if (!response.ok) {
        throw new Error(`${response.status}`);
      }
      const addedMember: UserGroupMember = await response.json();
      setMembers(current =>
        current.some(member => member.id === addedMember.id)
          ? current
          : [...current, addedMember].sort((left, right) =>
              left.displayName.localeCompare(right.displayName),
            ),
      );
      setMemberEmail('');
    } catch {
      setAddMemberError('Failed to add this user to the group');
    } finally {
      setAddMemberLoading(false);
    }
  };

  let helperText: string | undefined;
  if (hasError) {
    helperText = loadError ?? rawErrors?.join(', ');
  } else if (noGroups) {
    helperText =
      'No manageable persona groups were found. Membership in GP_GDL_<SPOKE>_SPOKE_ADMINS is required.';
  } else {
    helperText =
      (uiSchema?.['ui:help'] as string | undefined) ?? schema?.description;
  }

  return (
    <>
      <TextField
        select
        fullWidth
        margin="normal"
        label={schema?.title ?? 'User group'}
        required={required}
        disabled={loading || noGroups}
        error={hasError}
        value={formData ?? ''}
        onChange={event => {
          const group = options.find(
            option => option.displayName === event.target.value,
          );
          void selectGroup(group?.id ?? '', event.target.value);
        }}
        helperText={helperText}
        InputProps={{
          endAdornment: loading ? (
            <InputAdornment position="end">
              <CircularProgress color="inherit" size={20} />
            </InputAdornment>
          ) : undefined,
        }}
      >
        {options.map(group => (
          <MenuItem key={group.id} value={group.displayName}>
            {group.spokeName} - {group.displayName}
          </MenuItem>
        ))}
      </TextField>
      {showMemberManagement && selectedGroup && (
        <>
          <Typography variant="subtitle2" style={{ marginTop: 16 }}>
            Users in {selectedGroup.spokeName} - {selectedGroup.displayName}
          </Typography>
          {membersLoading && <CircularProgress size={20} />}
          {membersError && (
            <Typography color="error">{membersError}</Typography>
          )}
          {!membersLoading && !membersError && (
            <>
              <TextField
                fullWidth
                margin="normal"
                label="User email to add"
                value={memberEmail}
                onChange={event => setMemberEmail(event.target.value)}
                disabled={addMemberLoading}
                error={Boolean(addMemberError)}
                helperText={
                  addMemberError ??
                  "You can add users because you are a member of this spoke's SPOKE_ADMINS group."
                }
              />
              <Button
                variant="contained"
                color="primary"
                onClick={() => void addMember()}
                disabled={addMemberLoading || !memberEmail.trim()}
              >
                {addMemberLoading ? 'Adding...' : 'Add user to group'}
              </Button>
              <List dense>
                {members.length === 0 ? (
                  <ListItem>
                    <ListItemText primary="No users found in this group" />
                  </ListItem>
                ) : (
                  members.map(member => (
                    <ListItem key={member.id}>
                      <ListItemText
                        primary={member.displayName}
                        secondary={member.email}
                      />
                    </ListItem>
                  ))
                )}
              </List>
            </>
          )}
        </>
      )}
    </>
  );
};
